<template>
  <div class="container">
    <div class="row">
      <div class="col">
        <h3 class="text-center">
          <span style="padding: 0 12px;border-left:1px solid;border-right:1px solid;">{{ $t('series.title') }}</span>
        </h3>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <b-breadcrumb style="background-color: transparent;">
          <b-breadcrumb-item :to="{ name: 'index' }">{{ $t('index.title') }}</b-breadcrumb-item>
          <b-breadcrumb-item>{{ $t('series.title') }}</b-breadcrumb-item>
          <b-breadcrumb-item :to="{ name: 'series' }">{{ $t('series.detail.title') }}</b-breadcrumb-item>
        </b-breadcrumb>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12">
        <h4>{{ series.title_name }} {{ series.title_no }}</h4>
        <h5>{{ $t('series.detail.label.lecturer') }}：{{ series.lecr_name }}</h5>
        <p v-if="series.spkdate">{{ $t('series.detail.label.start-date') }}：{{ series.spkdate }}</p>
        <p v-if="series.rem && series.rem !== '(null)'" v-html="series.rem"></p>
      </div>
    </div>
    <social-sharing-button-toolbar
      :title="social_sharing_title"
      :to="social_sharing_route"
    ></social-sharing-button-toolbar>
    <div class="row mt-3">
      <div class="col-12">
        <h5>{{ $t('series.detail.list.handout.heading') }}</h5>
        <b-list-group>
          <b-list-group-item
            class="d-flex justify-content-between align-items-center"
            v-for="handout in handouts"
          >
            <span>
              {{ handout.ref_filename }}
              <span v-if="handout.ref_desc"><br>{{ handout.ref_desc }}</span>
              <span v-if="getHandoutDate(handout)"><br>{{ $t('series.detail.list.handout.label.updated-date') }}：{{ getHandoutDate(handout) }}</span>
            </span>
            <b-button :href="handout.ref_url" target="_blank">
              {{ $t('series.detail.list.handout.button.download.label') }} {{ handout.ref_filesize ? ` (${handout.ref_filesize})` : '' }}
            </b-button>
          </b-list-group-item>
        </b-list-group>
        <b-pagination
          v-model="handouts_page"
          :total-rows="handouts_total"
          :per-page="handouts_per_page"
          @change="pageChangeForHandouts"
          align="center"
          size="lg"
        ></b-pagination>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12">
        <h5>{{ $t('series.detail.list.episode.heading') }}</h5>
        <b-list-group>
          <b-list-group-item
            class="d-flex justify-content-between"
            v-for="episode in episodes"
          >
            <div>
              <h2>{{ episode.AV_fileorder }}{{ episode.AV_fileorder_sub ? episode.AV_fileorder_sub : '' }}</h2>
              <h4><b-badge variant="primary">{{ getEpisodeDate(episode) }}</b-badge></h4>
            </div>
            <b-button-group>
              <b-button
                @click="showAudiovisualPlayer(episode)"
              >{{ $t('series.detail.list.episode.button.stream.label') }}</b-button>
              <b-button
                @click="showAudiovisualList(episode)"
              >{{ $t('series.detail.list.episode.button.download.label') }}</b-button>
            </b-button-group>
          </b-list-group-item>
        </b-list-group>
        <b-modal
          centered
          size="lg"
          id="audiovisual-player"
          :title="series ? `${series.title_name} ${series.title_no}` : ''"
        >
          <series-audiovisual-player
            :episodes="all_episodes"
            :currently_playing_episode_index="episode_index"
            v-if="all_episodes.length > 0"
          ></series-audiovisual-player>
          <template #modal-footer="{ close }">
            <b-button class="w-100" variant="default" @click="close()">{{ $t('modal.button.close.label') }}</b-button>
          </template>
        </b-modal>
        <b-modal
          centered
          id="audiovisual-list"
          :title="series ? `${series.title_name} ${series.title_no}` : ''"
        >
          <series-audiovisual-efile-list :episode="episode"></series-audiovisual-efile-list>
          <template #modal-footer="{ close }">
            <b-button class="w-100" variant="default" @click="close()">{{ $t('modal.button.close.label') }}</b-button>
          </template>
        </b-modal>
        <b-pagination
          v-model="episodes_page"
          :total-rows="episodes_total"
          :per-page="episodes_per_page"
          @change="pageChangeForEpisodes"
          align="center"
          size="lg"
        ></b-pagination>
      </div>
    </div>
  </div>
</template>
<script>
import Vue from 'vue'

import axios from 'axios'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import dayjs from 'dayjs'
import VueAxios from 'vue-axios'

import { BadgePlugin } from 'bootstrap-vue'
import { BreadcrumbPlugin } from 'bootstrap-vue'
import { ButtonGroupPlugin } from 'bootstrap-vue'
import { ButtonPlugin } from 'bootstrap-vue'
import { ListGroupPlugin } from 'bootstrap-vue'
import { ModalPlugin } from 'bootstrap-vue'
import { PaginationPlugin } from 'bootstrap-vue'

import SocialSharingButtonToolbar from '@/components/button-toolbars/SocialSharingButtonToolbar'
import SeriesAudiovisualEfileList from '@/components/lists/SeriesAudiovisualEfileList'
import SeriesAudiovisualPlayer from '@/components/players/SeriesAudiovisualPlayer'

import i18n from '@/plugins/i18n.js'

import SeriesApiService from '@/services/budaedu-api-services/series-api-service.js'

Vue.use(axios)
Vue.use(BadgePlugin)
Vue.use(BreadcrumbPlugin)
Vue.use(ButtonGroupPlugin)
Vue.use(ButtonPlugin)
Vue.use(ListGroupPlugin)
Vue.use(ModalPlugin)
Vue.use(PaginationPlugin)
Vue.use(VueAxios)

Vue.use(SeriesApiService)

export default {
  i18n,
  name: 'series',
  props: {
    series_no: {
      type: String,
      required: true
    }    
  },
  data() {
    return {
      handouts_api_url: '',
      handouts_api_method: '',
      handouts_per_page: 5,
      handouts_page: 1,
      handouts_total: 0,
      handouts: [],
      episodes_api_url: '',
      episodes_api_method: '',
      episodes_per_page: 10,
      episodes_page: 1,
      episodes_total: 0,
      episodes: [],
      all_episodes: [],
      series: null,
      episode: null,
      episode_index: 0
    }
  },
  computed: {
    social_sharing_title() {
      return `${this.series.title_no} ${this.series.title_name}`;
    },
    social_sharing_route() {
      return { name: 'series', params: { series_no: this.series.title_no } }
    }
  },
  methods: {
    getHandoutDate(handout) {
      dayjs.extend(customParseFormat)

      return dayjs(handout.ref_filedate, 'YYYY-MM-DD HH:mm:ss', true).isValid()
        ? dayjs(handout.ref_filedate, 'YYYY-MM-DD HH:mm:ss', true).format('YYYY-MM-DD')
        : ''
    },
    getEpisodeDate(episode) {
      dayjs.extend(customParseFormat)

      let dates = []
      if (dayjs(episode.cdate, 'YYYY-MM-DD HH:mm:ss', true).isValid()) {
        dates.push(dayjs(episode.cdate, 'YYYY-MM-DD HH:mm:ss', true))
      }
      if (episode.VL_file_exist === 'Y') {
        dates.push(dayjs(episode.VL_filedate, 'YYYY-MM-DD HH:mm:ss', true))
      }
      if (episode.AL_file_exist === 'Y') {
        dates.push(dayjs(episode.AL_filedate, 'YYYY-MM-DD HH:mm:ss', true))
      }

      const earliestTime = dates.reduce((earliest, current) => {
        return current.isBefore(earliest) ? current : earliest;
      }, dates[0]);

      return earliestTime.format('YYYY-MM-DD')
    },
    pageChangeForHandouts(page) {
      this.handouts_page = page
      this.setHandouts()
    },
    pageChangeForEpisodes(page) {
      this.episodes_page = page
      this.setEpisodes()
    },
    showAudiovisualList(episode) {
      this.episode = episode
      this.$bvModal.show('audiovisual-list')
    },
    showAudiovisualPlayer(episode) {
      const episode_index = this.all_episodes.findIndex((item) => {
        return item.AV_fileindex === episode.AV_fileindex
      })
      this.episode_index = episode_index
      this.$bvModal.show('audiovisual-player')
      
      gtag('event', `watch series online`, {
        code: this.series.title_no,
        name: this.series.title_name
      })
    },
    async setSeries() { 
      const series = await this.$seriesApiService.getBySeriesNo(this.series_no)
      this.series = series.data
      await this.setSeriesRelatedResources(series.links)
    },
    async setSeriesRelatedResources(resource_links) {
      await Promise.all([
        this.setHandouts(resource_links.handouts.href, resource_links.handouts.type),
        this.setEpisodes(resource_links.episodes.href, resource_links.episodes.type),
        this.setAllEpisodes(resource_links.episodes.href, resource_links.episodes.type)
      ])
    },
    async setHandouts(url = '', method = '') {
      if (url != '') {
        this.handouts_api_url = url
      }
      if (method != '') {
        this.handouts_api_method = method
      }

      const { data } = await axios({
        url: `${this.handouts_api_url}?order=ref_filename,asc&per_page=${this.handouts_per_page}&page=${this.handouts_page}`,
        method: this.handouts_api_method
      })
      
      this.handouts = data.data
      this.handouts_total = data.meta.total
    },
    async setEpisodes(url = '', method = '') {
      if (url != '') {
        this.episodes_api_url = url
      }
      if (method != '') {
        this.episodes_api_method = method
      }

      const { data } = await axios({
        url: `${this.episodes_api_url}?order=AV_fileorder,asc|AV_fileorder_sub,asc&per_page=${this.episodes_per_page}&page=${this.episodes_page}`,
        method: this.episodes_api_method
      })
      
      this.episodes = data.data
      this.episodes_total = data.meta.total
    },
    async setAllEpisodes(url = '', method = '') {
      if (url != '') {
        this.episodes_api_url = url
      }
      if (method != '') {
        this.episodes_api_method = method
      }

      const { data: { data } } = await axios({
        url: `${this.episodes_api_url}?order=AV_fileorder,asc|AV_fileorder_sub,asc`,
        method: this.episodes_api_method
      })
      
      this.all_episodes = data
    }
  },
  async created() {
    //alert(`Vue version : ${Vue.version}`)
    await this.setSeries()
  },
  components: {
    SeriesAudiovisualEfileList,
    SeriesAudiovisualPlayer,
    SocialSharingButtonToolbar
  },
  metaInfo() {
    const title = this.series ? `${this.series.title_name} ${this.series.title_no}` : ''
    return {
      title: title,
      meta: [{
        vmid: 'description',
        name: 'description',
        content: ''
      }]
    }
  }
}
</script>